import React, { useState, useEffect, useMemo } from 'react';

/**
 * Local hero assets (add files under public/hero/):
 *   - uk-property-hero.jpg   — primary still (recommended min width 1920px)
 *   - uk-property-hero.mp4     — optional muted loop background (desktop only)
 *
 * If the local JPG is missing, the component falls back to a high-quality stock image
 * until you add your own photography.
 */
export const HERO_IMAGE_PUBLIC = `${process.env.PUBLIC_URL}/hero/uk-property-hero.png`;
const HERO_IMAGE_FALLBACK =
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=85';

export const HERO_VIDEO_PUBLIC = `${process.env.PUBLIC_URL}/hero/uk-property-hero.mp4`;

/** Set true after adding uk-property-hero.mp4 to public/hero/ */
const ENABLE_HERO_VIDEO = false;

const HeroBackground = () => {
  const [imgSrc, setImgSrc] = useState(HERO_IMAGE_PUBLIC);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mqNarrow = window.matchMedia('(max-width: 768px)');
    const sync = () => {
      setPrefersReducedMotion(mqReduce.matches);
      setIsNarrow(mqNarrow.matches);
    };
    sync();
    mqReduce.addEventListener('change', sync);
    mqNarrow.addEventListener('change', sync);
    return () => {
      mqReduce.removeEventListener('change', sync);
      mqNarrow.removeEventListener('change', sync);
    };
  }, []);

  const showVideo = useMemo(
    () => ENABLE_HERO_VIDEO && !prefersReducedMotion && !isNarrow,
    [prefersReducedMotion, isNarrow]
  );

  return (
    <div className="hero-background" aria-hidden="true">
      <img
        className={`hero-background__img${showVideo ? ' hero-background__img--behind-video' : ''}`}
        src={imgSrc}
        alt=""
        decoding="async"
        fetchPriority="high"
        onError={() => {
          if (imgSrc !== HERO_IMAGE_FALLBACK) setImgSrc(HERO_IMAGE_FALLBACK);
        }}
      />
      {showVideo && (
        <video
          className="hero-background__video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={HERO_IMAGE_FALLBACK}
        >
          <source src={HERO_VIDEO_PUBLIC} type="video/mp4" />
        </video>
      )}
      <div className="hero-background__overlay" />
      <div className="hero-background__fade-bottom" />
    </div>
  );
};

export default HeroBackground;
